import { PocApi } from '@Apis';
import MiniPagination from '@Report/MiniPagination';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Scrollbar,
  SearchInput,
  Space,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
  Truncate,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  AddIcon,
  CloseSIcon,
  DeleteIcon,
  RefreshIcon,
} from '@tonic-one/react-icons';
import { validateEmail } from '@Utils/common';
import { useDebounceState } from '@Utils/hooks';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../../../components/IconButton';
import { useOrgStore } from '../../../models/useOrgModel';
import { FileType } from '../../../types/globalSetting';
import { PocTransFer } from '../../Dashboard/ProvisionModal/PocTransFer';
import { useQueryPocUsers } from './hooks';
import config from '../../../config';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';
interface User {
  displayName: string;
  mail: string;
  id: string;
}

export function PocUserManagement() {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const { orgId } = useOrgStore();
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const { isFetching, data, refetch } = useQueryPocUsers({ orgId });
  const [globalFilter, setGlobalFilter] = useDebounceState('');
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({}); //manage your own row selection state
  const selectedUsers = useMemo(() => {
    return data.users.filter((user) => rowSelection[user.mail]);
  }, [rowSelection, data]);

  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: 10, //default page size
  });

  const columnHelper = createColumnHelper<User>();
  const columns = [
    columnHelper.display({
      id: 'select-col',
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          // disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      meta: {
        customColumnStyle: {
          width: '48px',
        },
      },
    }),
    // Display Column
    columnHelper.accessor('displayName', {
      id: 'displayName',
      header: t('common.label.user'),
      cell: (cell) => <>{cell.row.original.displayName}</>,
      meta: {
        customColumnStyle: {
          width: '240px',
        },
      },
    }),
    columnHelper.accessor('mail', {
      id: 'email',
      header: t('common.label.emailAddress'),
      cell: (cell) => <>{cell.row.original.mail}</>,
      meta: {
        customColumnStyle: {
          width: '100%',
        },
      },
    }),
  ];

  const table = useReactTable<User>({
    columns,
    data: data.users,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination,
      rowSelection,
      globalFilter, // pass our managed globalFilter state to the table
    },
    globalFilterFn: 'includesString',
    onGlobalFilterChange: setGlobalFilter,

    // row selection
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.mail,
    enableMultiRowSelection: true,

    //debug
    // debugTable: false,
    // debugHeaders: false,
    // debugColumns: false,
  });

  return (
    <Stack position="relative" height="100%" px="4x">
      <Flex marginY="4x" justifyContent="space-between">
        <Flex gap="8px">
          {selectedUsers.length > 0 ? (
            <>
              <Button
                variant="secondary"
                columnGap="2x"
                onClick={() => setDeleteModalOpen(true)}
              >
                <DeleteIcon />
                {t('common.action.label.delete')}
              </Button>
              <Button variant="ghost" onClick={() => setRowSelection({})}>
                <CloseSIcon />
                {t('common.label.itemSelected', {
                  NUMBER: selectedUsers.length,
                })}
              </Button>
            </>
          ) : (
            <>
              <AddUserButton
                onClick={() => setAddModalOpen(true)}
                disabled={data.users.length >= 100}
              />

              <ImportUserButton
                orgId={orgId}
                disabled={data.users.length >= 100}
              />
              <SearchInput
                width={374}
                placeholder={t('globalSetting.pocUser.searchPlaceholder')}
                onClearInput={() => table.setGlobalFilter('')}
                onChange={(e) => table.setGlobalFilter(e.target.value)}
              />
            </>
          )}
        </Flex>
        <IconButton onClick={() => refetch()}>
          <RefreshIcon />
        </IconButton>
      </Flex>
      <Box position="relative" height="calc(100% - 96px)" width="100%">
        {isFetching && <TableSpinner />}
        <Table layout="flexbox" pb="64px" height="100%" width="100%">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} width="100%">
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                    ...header.column.columnDef.meta?.customColumnStyle,
                  };
                  return (
                    <TableCell key={header.id} {...styleProps}>
                      {header.isPlaceholder ? null : (
                        <Truncate>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Truncate>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <Scrollbar height="100%" maxHeight="100%" overflow="scroll">
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  _hover={{
                    backgroundColor: colorStyle.background.highlighted,
                  }}
                  height="40px"
                  width="100%"
                >
                  {row.getVisibleCells().map((cell) => {
                    const styleProps = {
                      minWidth: cell.column.columnDef.minSize,
                      width: cell.column.getSize(),
                      ...cell.column.columnDef.meta?.customColumnStyle,
                    };
                    return (
                      <TableCell key={cell.id} {...styleProps}>
                        <Truncate>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Truncate>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Scrollbar>
        </Table>
      </Box>
      <Flex
        width="calc(100% + 16px)"
        position="absolute"
        bottom="0"
        justifyContent="flex-end"
        py="16px"
        px="24px"
        backgroundColor={colorStyle.background.secondary}
      >
        <MiniPagination
          totalCount={table.getRowCount()}
          currentPage={pagination.pageIndex + 1}
          pageSize={pagination.pageSize}
          changeCurrentPage={(v: number) => {
            // MiniPagination first page index is 1
            setPagination({ ...pagination, pageIndex: v - 1 });
          }}
          changePageSize={(size: number) => {
            setPagination({ ...pagination, pageSize: size });
          }}
        />
      </Flex>
      <AddUserModal
        isOpen={isAddModalOpen}
        onClose={() => setAddModalOpen(false)}
        successCallback={() => {
          setRowSelection({});
          refetch();
        }}
        orgId={orgId}
      />
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        selectedUsers={selectedUsers}
        orgId={orgId}
        successCallback={() => {
          setRowSelection({});
          refetch();
        }}
      />
    </Stack>
  );
}

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  successCallback: () => void;
}
const AddUserModal = ({
  orgId,
  isOpen,
  onClose,
  successCallback,
}: AddUserModalProps) => {
  const { t } = useTranslation();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const exchangePocUserLimit = useMemo(() => {
    return (
      (selectedOrgGlobalSetting.exchangePocUserLimit as number) ??
      config.ui.globalSettings.limitation.exchangePocUserLimit
    );
  }, [selectedOrgGlobalSetting]);
  const pocUsersRef = useRef<{
    //PocUsers
    data: object[];
  }>({
    data: [],
  });

  const onConfirm = async () => {
    const selectedCount = pocUsersRef.current.data.length;
    if (selectedCount === 0) {
      window.showNotification({
        severity: 'error',
        titleMsg: t('errors.PROVISION;POC_SELECT_NO_USER'),
      });
      return;
    }
    if (selectedCount > exchangePocUserLimit) {
      window.showNotification({
        severity: 'error',
        titleMsg: t('errors.PROVISION;POC_EXCEED_USER_LIMIT', {
          exceeded: selectedCount - exchangePocUserLimit,
          limit: exchangePocUserLimit,
        }),
      });
      return;
    }
    console.log(pocUsersRef.current.data);
    try {
      await PocApi.extendPocUsers(orgId, pocUsersRef.current.data);
      window.showNotification({
        titleMsg: t('globalSetting.pocUser.add.success'),
        severity: 'success',
      });
      successCallback();
      onClose();
    } catch (e: any) {
      const errorDetail = e?.data?.error?.details?.[0];
      if (
        errorDetail?.code === 'MaxLimitExceeded' &&
        errorDetail?.target === 'users'
      ) {
        window.showNotification({
          severity: 'error',
          titleMsg: t('errors.PROVISION;POC_EXCEED_USER_LIMIT_IMPORT', {
            currentCount: errorDetail.currentCount,
            importCount: selectedCount,
            limit: errorDetail.limit,
          }),
        });
      } else {
        window.showNotification({
          severity: 'error',
          titleMsg: t('common.unknownError'),
        });
      }
    }
  };

  return (
    <Modal isOpen={isOpen} isClosable size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('globalSetting.pocUser.add.title')}</ModalHeader>
        <ModalBody>
          <Text>
            {t('provision:provision.service.exchange.poc.selectUsersDesc', {
              count: exchangePocUserLimit,
            })}
          </Text>
          <Box width="100%" my="12px">
            <PocTransFer
              ref={pocUsersRef}
              orgId={orgId}
              toRightStyle={{ marginX: '24px' }}
            />
          </Box>
        </ModalBody>
        <ModalFooter justifyContent="flex-end" gap="8px">
          <Button variant="primary" onClick={onConfirm}>
            {t('common.action.label.save')}
          </Button>

          <Button onClick={onClose} minWidth="20x">
            {t('common.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

interface DeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  selectedUsers: { mail: string; displayName: string; id: string }[];
  successCallback: () => void;
}
const DeleteUserModal = ({
  isOpen,
  onClose,
  orgId,
  selectedUsers,
  successCallback,
}: DeleteUserModalProps) => {
  const { t } = useTranslation();

  const onDelete = async () => {
    await PocApi.deletePocUsers(orgId, selectedUsers);
    window.showNotification({
      titleMsg: t('globalSetting.pocUser.delete.success'),
      severity: 'success',
    });
    successCallback();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} isClosable size="sm" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('globalSetting.pocUser.delete.title')}</ModalHeader>
        <ModalBody>
          <Text>{t('globalSetting.pocUser.delete.desc1')}</Text>
          <Space height="20px" />
          <Text>{t('globalSetting.pocUser.delete.desc2')}</Text>
        </ModalBody>
        <ModalFooter justifyContent="flex-end" gap="8px">
          <Button variant="primary" onClick={onDelete}>
            {t('common.action.label.delete')}
          </Button>
          <Button onClick={onClose} minWidth="20x">
            {t('common.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ImportUserButton = ({
  orgId,
  disabled,
}: {
  orgId: string;
  disabled: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const onImport = () => {
    // readfile content
    if (!file || FileType.TXT !== file.type) return;

    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = async (e) => {
      if (!e.target) return;

      setIsLoading(true);

      const upns = (e.target.result as string)
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean)
        .filter((upn) => validateEmail(upn));

      const importCount = upns.length;
      PocApi.importUsers(orgId, upns)
        .then(() => {
          window.showNotification({
            severity: 'success',
            titleMsg: t('globalSetting.pocUser.import.msg'),
          });
        })
        .catch((e: any) => {
          const errorDetail = e?.data?.error?.details?.[0];
          if (
            errorDetail?.code === 'MaxLimitExceeded' &&
            errorDetail?.target === 'users'
          ) {
            window.showNotification({
              severity: 'error',
              titleMsg: t('errors.PROVISION;POC_EXCEED_USER_LIMIT_IMPORT', {
                currentCount: errorDetail.currentCount,
                importCount,
                limit: errorDetail.limit,
              }),
            });
          } else {
            window.showNotification({
              severity: 'error',
              titleMsg: t('common.unknownError'),
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
      setIsOpen(false);
    };
  };

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setIsOpen(true)}
        disabled={disabled}
      >
        {t('common.action.label.import')}
      </Button>
      <Modal
        isOpen={isOpen}
        isClosable
        size="sm"
        onClose={() => setIsOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('globalSetting.pocUser.import.title')}</ModalHeader>
          <ModalBody>
            <Button
              variant="secondary"
              onClick={() => {
                if (inputRef.current) {
                  inputRef.current.click();
                }
              }}
            >
              {t('common.action.label.chooseFile')}
            </Button>
            <input
              ref={inputRef}
              type="file"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
              }}
              onClick={(e: any) => (e.target.value = null)}
              hidden
              accept=".txt"
            />
            <Space width="10px" />
            {file ? file.name : t('common.action.chooseFile.noChosen')}

            {file && FileType.TXT !== file.type && (
              <Text pt="4px" color={colorStyle.color.error}>
                {t('globalSetting.pocUser.import.errorFormat')}
              </Text>
            )}
            <Box color={colorStyle.color.secondary} mt="16px">
              <Text>{t('common.label.note')}:</Text>
              <Text as="li" display="list-item">
                {t('globalSetting.pocUser.import.note1')}
              </Text>
              <Text as="li" display="list-item">
                {t('globalSetting.pocUser.import.note2')}
              </Text>
              <Text as="li" display="list-item">
                {t('globalSetting.pocUser.import.note3')}
              </Text>
              <Text as="li" display="list-item">
                {t('globalSetting.pocUser.import.note4')}
              </Text>
            </Box>
          </ModalBody>
          <ModalFooter justifyContent="flex-end" gap="8px">
            <Button
              variant="primary"
              onClick={onImport}
              disabled={!file || FileType.TXT !== file.type || isLoading}
            >
              {isLoading && <Spinner size="xs" />}
              {t('common.action.label.import')}
            </Button>
            <Button
              onClick={() => {
                setFile(null);
                setIsOpen(false);
              }}
              minWidth="20x"
            >
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const AddUserButton = ({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Popover disabled={!disabled} trigger="hover" placement="top">
      <PopoverTrigger>
        <Box>
          <Button
            variant="primary"
            columnGap="2x"
            disabled={disabled}
            onClick={onClick}
          >
            <AddIcon />
            {t('common.action.label.add')}
          </Button>
        </Box>
      </PopoverTrigger>
      <PopoverContent PopperProps={{ usePortal: true }}>
        {t('globalSetting.pocUser.add.tooltip')}
      </PopoverContent>
    </Popover>
  );
};

const TableSpinner = () => (
  <Spinner
    position="absolute"
    top="50%"
    left="50%"
    transform="translate(-50%,-50%)"
  />
);
